/**
 * @Author: karen Yunuen Pozos Arias
 * @JobTitle : Full Stack Developer
 * @Email : kpozos@ibl.mx
 */
import {getFullName, isEmailFormat} from "../../../Apis/functions";
import {IconChipMultiplePerson, IconPersonEmail} from "../../../components/buttons/IconButtons";
import {RELATIONSHIP_TYPE, RESIDENT_TYPE} from "../../Building/Model/Model";
import {
    INVITABLE_VISITOR_TYPES,
    NONE_VISITOR_TYPE,
    normalizeVisitorTypeName
} from "../../Dashboard/Visitor/Type/Model/Model";

export const MY_APARTMENTS_TABS_MODEL = {
    HOUSEHOLD_MEMBERS: {
        id: 0,
        key: "HOUSEHOLD_MEMBERS",
        translation: "household_members",
        icon: <IconChipMultiplePerson width={20} height={20}/>
    },
    INVITATIONS: {
        id: 1,
        key: "INVITATIONS",
        translation: "invitations",
        icon: <IconPersonEmail width={20} height={20}/>
    }
}

/**
 * Resolves the visitor type of the catalog the card picked stands for, the cards
 * are named after the types visitor-type-service returns, so the key of the card
 * is matched against the name of the type once both are normalized.
 *
 * @param {Array<Object>} catalog - Visitor types returned by the catalog.
 * @param {string} key - Key of the card the resident picked.
 * @returns {number|null} Id of the type matched, the NONE one when the catalog does not hold it.
 */
export const getVisitorTypeIdByKey = (catalog, key) => {
    const types = catalog ?? [];

    return types.find((type) => normalizeVisitorTypeName(type?.name) === normalizeVisitorTypeName(key))?.id
        ?? types.find((type) => normalizeVisitorTypeName(type?.name) === NONE_VISITOR_TYPE)?.id
        ?? null;
}

/**
 * Resolves the card the visitor already registered was created with, the list
 * returns their type either as the object of the catalog or as its id, so both
 * are resolved into the name the cards are matched against.
 *
 * @param {Array<Object>} catalog - Visitor types returned by the catalog.
 * @param {Object} [visitor] - Visitor picked on the chip search.
 * @returns {Object|null} Card of the type of the visitor, null when it cannot be resolved.
 */
export const getVisitorTypeCardByVisitor = (catalog, visitor) => {
    if (!visitor) return null;

    const typeId = visitor?.visitorType?.id ?? visitor?.visitorTypeId;
    const name = visitor?.visitorType?.name
        ?? visitor?.visitorTypeName
        ?? (catalog ?? []).find((type) => type?.id === typeId)?.name;

    if (!name) return null;

    return INVITABLE_VISITOR_TYPES
        .find((card) => normalizeVisitorTypeName(card.key) === normalizeVisitorTypeName(name)) ?? null;
}

/**
 * Visitors already registered the chip search of the invite external user modal
 * offers. The list identifies them by visitorId -the one the create invitation
 * endpoint expects in visitors- and returns their name already joined in
 * visitorFullName and their email in visitorEmail, so the contact of the second
 * step is resolved from them: the first word of the name is the name and the
 * rest the last name.
 */
export const MY_APARTMENT_VISITOR_OPTION_MODEL = {
    /**
     * @param {Object} visitor - Visitor coming from the get all visitors list.
     * @returns {Object} Visitor with the id, the full name and the contact the modal reads.
     */
    set: function (visitor) {
        const fullName = (visitor?.visitorFullName
            || getFullName(visitor?.name, visitor?.lastName, visitor?.secondLastName)
            || '').replace(/\s+/g, ' ').trim();
        const [name = '', ...lastName] = fullName.split(' ');

        return {
            ...visitor,
            id: visitor?.visitorId ?? visitor?.userId ?? visitor?.id,
            fullName,
            name: visitor?.name || name,
            lastName: visitor?.lastName || lastName.join(' '),
            email: visitor?.visitorEmail || visitor?.email || ''
        }
    },
    /**
     * @param {Array<Object>} content - Visitors returned by the list.
     * @returns {Array<Object>} Visitors ready to be turned into options of the chip.
     */
    setList: function (content) {
        return (content || []).map((item) => this.set(item));
    }
}

/**
 * TODO: temporary options of the relationship autocomplete, replace them with the
 *       relationships already registered once building-service exposes them.
 *
 * Relationships offered while the resident types, the owner is not one of them
 * because it belongs to the main resident of the unit.
 */
export const MY_RELATIONSHIP_TYPES = Object.values(RELATIONSHIP_TYPE)

/**
 * Form model of the add household member modal. The relationship is stored as
 * the RELATIONSHIP_TYPE value of the option picked, the card translates it.
 *
 * The payload of building-service/v1/resident/create needs the apartment the
 * member is registered on and the resident that registers them, both come from
 * the building of the logged resident. Every member added from here is a
 * FAMILY_MEMBER, the form only asks for their relationship.
 */
export const MY_HOUSEHOLD_MEMBER_MODEL = {
    name: '',
    lastName: '',
    email: '',
    relationship: '',
    isValid: function () {
        return this.name !== null && this.name !== ''
            && this.lastName !== null && this.lastName !== ''
            && this.relationship !== null && this.relationship !== ''
            && this.email !== null && this.email !== '' && isEmailFormat(this.email);
    },
    toRegister: function (buildingApartmentId, residentId) {
        return {
            buildingApartmentId,
            residentId,
            name: this.name,
            lastName: this.lastName,
            email: this.email,
            residentType: RESIDENT_TYPE.FAMILY_MEMBER,
            relationshipType: this.relationship
        }
    }
}

/**
 * Steps of the invite external user modal, the modal renders one at a time and
 * the footer confirms on the last one.
 */
export const MY_APARTMENT_INVITATION_STEPS = {
    VISITOR_TYPE: 1,
    VISITOR_DATA: 2,
    SCHEDULE: 3
}

/**
 * Form model of the invite external user modal, it holds the data of the two
 * steps that come after the visitor type, the contact of the visitor and the
 * schedule of the visit with its instructions.
 */
export const MY_APARTMENT_INVITATION_MODEL = {
    name: '',
    lastName: '',
    email: '',
    startDateAtTime: null,
    endDateAtTime: null,
    instructions: '',
    isVisitorDataValid: function () {
        return this.name !== null && this.name !== ''
            && this.lastName !== null && this.lastName !== ''
            && (this.email === null || this.email === '' || isEmailFormat(this.email));
    },
    isScheduleValid: function () {
        return this.startDateAtTime !== null && this.startDateAtTime !== ''
            && this.endDateAtTime !== null && this.endDateAtTime !== ''
            && new Date(this.endDateAtTime) > new Date(this.startDateAtTime);
    },
    /**
     * Payload of invitation-service/v1/web/create. The visitor already travels
     * registered, the modal pre registers it before creating the invitation when
     * it was captured by hand.
     *
     * The resident invites one visitor at a time and never asks for the
     * documentation validation, so the visitor number is always one and the
     * validation is off with the visitor type resolved for it.
     *
     * @param {Object} params
     * @param {string} params.hostId - Logged resident, the host of the visit.
     * @param {string} params.visitorId - User of the visitor invited.
     * @param {string} params.zoneId - Zone of the apartment the visit is created on.
     * @param {number} params.visitorTypeId - Visitor type of the catalog resolved from the card picked.
     * @param {number|string|null} params.accessToleranceMin - Tolerance of the system config.
     * @returns {Object} Body of the create invitation endpoint.
     */
    toRegister: function ({ hostId, visitorId, zoneId, visitorTypeId, accessToleranceMin }) {
        return {
            hostId,
            visitors: [visitorId],
            startDate: new Date(this.startDateAtTime).getTime(),
            endDate: new Date(this.endDateAtTime).getTime(),
            zoneId,
            visitorNumber: 1,
            visitorType: { id: visitorTypeId },
            documentationValidated: false,
            accessToleranceMin: (accessToleranceMin === '' || accessToleranceMin == null)
                ? null
                : Number(accessToleranceMin),
            // The instructions the resident wrote on the last step travel as the
            // description of the invitation, they are optional and the service
            // rejects them empty
            description: this.instructions || null
        }
    },
    /**
     * Payload of authentication-service/v4/pre-register, the user of a visitor
     * that is not registered yet. The email captured on the second step is not
     * part of it, the service generates the one the visitor is created with.
     *
     * @returns {Object} Body of the pre register endpoint.
     */
    toPreRegister: function () {
        return {
            name: this.name,
            lastName: this.lastName
        }
    }
}

/**
 * Apartment of the logged resident rendered by the information card of the
 * details view, filled with the apartment returned by
 * building-service/public/v1/building-apartment/details/{id}.
 *
 * The address is not part of the response, the view resolves it from the
 * coordinates of the building. The id of the apartment travels with the state
 * of the route, the detail does not return it.
 */
export const MY_APARTMENT_DETAIL_MODEL = {
    id: null,
    buildingId: '',
    buildingApartmentId: '',
    residentId: '',
    buildingName: '',
    apartmentName: '',
    zoneId: '',
    latitude: '',
    longitude: '',
    location: '',
    mainResident: '',
    email: '',
    phoneNumber: '',
    statusId: '',
    /**
     * Fills the fields the detail endpoint returns, the ones that come from the
     * card of the list are kept by the view.
     *
     * @param {Object} data - Apartment coming from the backend.
     * @returns {Object} Fields of the detail ready to be rendered.
     */
    set: function (data) {
        return {
            buildingName: data?.buildingName ?? '',
            apartmentName: data?.apartment ?? '',
            zoneId: data?.zoneId ?? '',
            latitude: data?.latitude ?? '',
            longitude: data?.longitude ?? '',
            mainResident: data?.ownerFullName ?? '',
            email: data?.email ?? '',
            phoneNumber: data?.phoneNumber ?? '',
            statusId: data?.statusId ?? '',
            // Resident of the apartment the household members are registered
            // under, the create resident endpoint sends it as the residentId
            residentId: data?.residentId ?? ''
        }
    }
}

/**
 * Visit of the resident the invitations tab lists, one row of the user_invitation
 * search the apartment runs with the INVITATION_ZONE_ID view type.
 *
 * The table paints one name for the visitor and one for the resident that
 * invited them, the search returns the visitor already joined and the host split
 * in its three parts. The rest of the row travels untouched, the details modal
 * reads the zone, the period, the type of visitor and the description of the
 * visit from it.
 */
export const MY_APARTMENT_VISIT_MODEL = {
    /**
     * Fills the fields the table and the details modal render.
     *
     * @param {Object} data - Invitation coming from the search.
     * @returns {Object} Invitation with the names already joined.
     */
    set: function (data) {
        return {
            ...data,
            visitorFullName: data?.visitorFullName
                || getFullName(data?.visitorName, data?.visitorLastName, data?.visitorSecondLastName),
            createdBy: getFullName(data?.hostName, data?.hostLastName, data?.hostSecondLastName)
        }
    },
    /**
     * Maps the page returned by the search.
     *
     * @param {Array<Object>} content - Invitations of the page.
     * @returns {Array<Object>} Invitations ready to be rendered.
     */
    setList: function (content) {
        return (content || []).map((item) => this.set(item));
    }
}
